import React, { useMemo } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ClientScheduleSlot, WEEKDAYS } from "@/types/staffing/serviceRequest";
import {
  MIDNIGHT_END,
  TimeOption,
  generateTimeOptions,
  getScheduleTotalHours,
  newScheduleSlot,
  timeToMinutes,
} from "../clientScheduleUtils";

interface ClientSchedulePickerProps {
  id: string;
  value: ClientScheduleSlot[];
  onChange: (slots: ClientScheduleSlot[]) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** How many time ranges a single day may carry. Defaults to unlimited. */
  maxRangesPerDay?: number;
}

/**
 * Weekly day/time picker: tick a day, then pick a start and end time for it.
 * A day may carry more than one time range unless `maxRangesPerDay` limits it.
 */
export function ClientSchedulePicker({
  id,
  value,
  onChange,
  disabled = false,
  invalid = false,
  maxRangesPerDay = Number.POSITIVE_INFINITY,
}: ClientSchedulePickerProps) {
  const timeOptions = useMemo(() => generateTimeOptions(), []);
  const startOptions = useMemo(
    () => timeOptions.filter((o) => o.value !== MIDNIGHT_END),
    [timeOptions],
  );

  const toggleDay = (day: number, checked: boolean) => {
    if (checked) onChange([...value, newScheduleSlot(day)]);
    else onChange(value.filter((s) => s.day !== day));
  };

  const addSlot = (day: number) => onChange([...value, newScheduleSlot(day)]);

  const removeSlot = (slotId: string) =>
    onChange(value.filter((s) => s.id !== slotId));

  const updateSlot = (slotId: string, patch: Partial<ClientScheduleSlot>) =>
    onChange(
      value.map((s) => {
        if (s.id !== slotId) return s;
        const next = { ...s, ...patch };
        // A new start at or after the chosen end invalidates the end.
        if (
          patch.start !== undefined &&
          next.end !== "" &&
          timeToMinutes(next.end) <= timeToMinutes(next.start)
        ) {
          next.end = "";
        }
        return next;
      }),
    );

  const selectedDays = new Set(value.map((s) => s.day)).size;
  const totalHours = getScheduleTotalHours(value);

  return (
    <div
      id={id}
      className={cn(
        "rounded-md border border-input bg-background divide-y divide-border overflow-hidden",
        invalid && "border-destructive",
        disabled && "opacity-70",
      )}
    >
      {WEEKDAYS.map(({ day, label }) => {
        const daySlots = value.filter((s) => s.day === day);
        const checked = daySlots.length > 0;
        const dayCheckboxId = `${id}-day-${day}`;

        return (
          <div
            key={day}
            className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-start"
          >
            <div className="flex items-center gap-2 shrink-0 sm:w-32 sm:h-10">
              <Checkbox
                id={dayCheckboxId}
                checked={checked}
                onCheckedChange={(c) => toggleDay(day, c)}
                disabled={disabled}
              />
              <Label
                htmlFor={dayCheckboxId}
                className={cn(
                  "text-sm font-medium cursor-pointer",
                  !checked && "text-muted-foreground font-normal",
                )}
              >
                {label}
              </Label>
            </div>

            {checked ? (
              <div className="flex flex-1 flex-col gap-2">
                {daySlots.map((slot, index) => {
                  const endOptions = slot.start
                    ? timeOptions.filter(
                        (o) => timeToMinutes(o.value) > timeToMinutes(slot.start),
                      )
                    : timeOptions;
                  const isLast = index === daySlots.length - 1;
                  const canAddMore = daySlots.length < maxRangesPerDay;

                  return (
                    <div key={slot.id} className="flex items-center gap-2">
                      <TimeSelect
                        ariaLabel={`${label} start time`}
                        value={slot.start}
                        options={startOptions}
                        placeholder="Start"
                        disabled={disabled}
                        onChange={(start) => updateSlot(slot.id, { start })}
                      />
                      <span className="text-xs text-muted-foreground shrink-0">to</span>
                      <TimeSelect
                        ariaLabel={`${label} end time`}
                        value={slot.end}
                        options={endOptions}
                        placeholder="End"
                        disabled={disabled}
                        onChange={(end) => updateSlot(slot.id, { end })}
                      />
                      <div className="flex items-center shrink-0">
                        {isLast && canAddMore && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title={`Add another time range on ${label}`}
                            aria-label={`Add another time range on ${label}`}
                            disabled={disabled}
                            onClick={() => addSlot(day)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        )}
                        {daySlots.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            title="Remove this time range"
                            aria-label={`Remove time range on ${label}`}
                            disabled={disabled}
                            onClick={() => removeSlot(slot.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground sm:self-center">
                Not available
              </span>
            )}
          </div>
        );
      })}

      <div className="flex items-center justify-between bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <span>
          {selectedDays === 0
            ? "No days selected"
            : `${selectedDays} ${selectedDays === 1 ? "day" : "days"} selected`}
        </span>
        <span>{formatHours(totalHours)} / week</span>
      </div>
    </div>
  );
}

function formatHours(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded} ${rounded === 1 ? "hr" : "hrs"}`;
}

interface TimeSelectProps {
  ariaLabel: string;
  value: string;
  options: TimeOption[];
  placeholder: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

function TimeSelect({
  ariaLabel,
  value,
  options,
  placeholder,
  disabled,
  onChange,
}: TimeSelectProps) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger aria-label={ariaLabel} className="h-9 w-[9.5rem] text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
